import type { FC } from "react";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import { useTranslation } from "@/shared/hooks";
import { openAssistant } from "../model/assistantStore";
import { SparklesIcon } from "./SparklesIcon";

// Пузырь стоит над cookie-баннером: баннер пишет свою высоту в эту
// CSS-переменную, пока открыт (CookieBanner.tsx).
export const ASSISTANT_BOTTOM_OFFSET =
  "calc(16px + var(--cookie-banner-height, 0px))";

// Выше шапки (1200) и route-диалога (1199), ниже модалок MUI (1300): окно
// входа должно открываться поверх панели.
export const ASSISTANT_Z_INDEX = 1250;

const SIZE = 60;
const ICON = 28;

interface AssistantBubbleProps {
  name: string;
  hidden: boolean;
}

/**
 * Кнопка помощника в углу витрины: круг в основном цвете площадки со знаком
 * «Искры». При наведении (или фокусе с клавиатуры) плавно раскрывается в
 * пилюлю с подписью «Спросить {name}»; на тач-экранах остаётся кругом.
 */
export const AssistantBubble: FC<AssistantBubbleProps> = ({ name, hidden }) => {
  const { t } = useTranslation();
  if (hidden) return null;

  const label = t("assistant.open", { name });
  const expanded = {
    "& .assistant-label": { maxWidth: 240, opacity: 1, ml: 1.25 },
    "& .assistant-spark": { transform: "scale(1.3) rotate(45deg)" },
    boxShadow: 10,
  };

  return (
    <Fab
      color="primary"
      aria-label={label}
      onClick={() => openAssistant()}
      sx={{
        position: "fixed",
        right: 16,
        bottom: ASSISTANT_BOTTOM_OFFSET,
        zIndex: ASSISTANT_Z_INDEX,
        width: "auto",
        minWidth: SIZE,
        height: SIZE,
        px: `${(SIZE - ICON) / 2}px`,
        borderRadius: `${SIZE / 2}px`,
        textTransform: "none",
        boxShadow: 6,
        transition: (theme) =>
          theme.transitions.create(["box-shadow", "background-color"], {
            duration: theme.transitions.duration.short,
          }),
        "& .assistant-label": {
          maxWidth: 0,
          opacity: 0,
          ml: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1,
          transition: (theme) =>
            theme.transitions.create(["max-width", "opacity", "margin"], {
              duration: 280,
              easing: theme.transitions.easing.easeOut,
            }),
        },
        "& .assistant-spark": {
          transformBox: "fill-box",
          transformOrigin: "center",
          transition: (theme) =>
            theme.transitions.create("transform", { duration: 400 }),
        },
        "@media (hover: hover)": { "&:hover": expanded },
        "&:focus-visible": {
          ...expanded,
          outline: "2px solid",
          outlineColor: "primary.contrastText",
          outlineOffset: 2,
        },
      }}
    >
      <SparklesIcon size={ICON} />
      <Box component="span" className="assistant-label" aria-hidden>
        {label}
      </Box>
    </Fab>
  );
};
